import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useAutomation,
  useAutomations,
  useCreateAutomation,
  useUpdateAutomation,
} from '../../api/automations';
import { useChannels } from '../../api/channels';
import { usePipelines } from '../../api/pipelines';
import { useWhatsappTemplates } from '../../api/whatsapp-templates';
import { useAutomationFlow, useAutomationFlows } from '../../api/automation-flows';
import {
  blankAction,
  blankForm,
  describeAutomation,
  formFromAutomation,
  knownReplyOptions,
  toPayload,
  validateForm,
  type ActionKind,
  type AutomationFormState,
} from './automation-model';
import { LinkButton, box, muted } from './automation-form-parts';
import { AutomationDefinitionEditor } from './AutomationDefinitionEditor';
import { FlowPreviewPanel } from './builder/FlowPreviewPanel';

/**
 * Create / edit an automation. The When / If / Then is edited in
 * `AutomationDefinitionEditor` - the visual builder by default (like
 * conversation flows), the step-by-step form, or JSON; all three edit the
 * same state, which `automation-model.ts` maps to the API payload. Name,
 * on/off and a live plain-English summary sit below, whichever view is open.
 */

/** The steps of the flow a "Start a guided conversation" step runs (side panel). */
function TenantFlowPreview({
  flowKey,
  flows,
}: {
  flowKey: string;
  flows: { id: string; key: string; name: string; tenantId: string | null }[];
}) {
  const item = flows.find((f) => f.key === flowKey);
  const flow = useAutomationFlow(item?.id);
  if (!item) return null;
  return (
    <FlowPreviewPanel
      name={item.name}
      definition={flow.data?.definition}
      loading={flow.isPending}
      openHref={item.tenantId ? `/automation-flows/${item.id}/edit` : '/automation-flows'}
    />
  );
}

export function AutomationFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const existing = useAutomation(id);
  const allAutomations = useAutomations();
  const create = useCreateAutomation();
  const update = useUpdateAutomation(id ?? '');
  // Every channel an automation can listen on - WhatsApp numbers and website chats.
  const channels = useChannels();
  const pipelines = usePipelines();
  const flows = useAutomationFlows();

  const [form, setForm] = useState<AutomationFormState>(blankForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [jsonValid, setJsonValid] = useState(true);

  useEffect(() => {
    if (isEdit && existing.data) setForm(formFromAutomation(existing.data));
  }, [isEdit, existing.data]);

  const templates = useWhatsappTemplates({ channelId: form.channelId || undefined });
  const approvedTemplates = useMemo(() => {
    const seen = new Set<string>();
    return (templates.data?.items ?? []).filter((t) => {
      if (t.status !== 'APPROVED' || seen.has(t.name)) return false;
      seen.add(t.name);
      return true;
    });
  }, [templates.data]);

  const replyOptions = useMemo(() => {
    const others = (allAutomations.data?.items ?? []).filter((a) => a.id !== id);
    const own = knownReplyOptions([
      {
        name: form.name || 'this automation',
        actions: toPayload(form).actions.map((a) => ({
          ...a,
          config: a.config as Record<string, unknown>,
        })),
      },
    ]);
    return [...own, ...knownReplyOptions(others)];
  }, [allAutomations.data, id, form]);

  const summary = useMemo(() => {
    const payload = toPayload(form);
    return describeAutomation(
      {
        triggerTypes: form.triggers,
        conditions: (payload.conditions ?? []).map((c) => ({ ...c, value: c.value ?? null })),
        actions: payload.actions.map((a) => ({
          ...a,
          config: a.config as Record<string, unknown>,
        })),
      },
      {
        flowName: (key) => flows.data?.items.find((f) => f.key === key)?.name,
        buttonTitles: Object.fromEntries(replyOptions.map((o) => [o.id, o.title])),
      },
    );
  }, [form, flows.data, replyOptions]);

  if (isEdit && existing.isPending)
    return <PageState kind="loading" message="Loading automation…" />;
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  const set = (patch: Partial<AutomationFormState>) => setForm((f) => ({ ...f, ...patch }));
  const clear = (...keys: string[]) =>
    setErrors((e) => {
      const next = { ...e };
      for (const k of keys) delete next[k];
      return next;
    });
  const busy = create.isPending || update.isPending;

  /** A new step; "add as a lead" starts in the default pipeline's first stage. */
  function newAction(kind: ActionKind) {
    const action = blankAction(kind);
    if (kind === 'lead') {
      const pipeline = pipelines.data?.find((p) => p.isDefault) ?? pipelines.data?.[0];
      action.pipelineId = pipeline?.id ?? '';
      action.stageId = pipeline?.stages[0]?.id ?? '';
    }
    return action;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!jsonValid) {
      setFormError('Fix the JSON first - or switch back to the visual builder.');
      toast.error('Fix the JSON first.');
      return;
    }
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length) {
      if (found.name || found.description) setShowDetails(true);
      setFormError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }
    try {
      const payload = toPayload(form);
      if (isEdit && id) await update.mutateAsync(payload);
      else await create.mutateAsync(payload);
      toast.success(isEdit ? 'Automation updated.' : 'Automation created.');
      navigate('/automations', { replace: true });
    } catch (err) {
      const fieldErrors = fieldErrorsFromApiError(err);
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
        setFormError('Please fix the highlighted fields.');
        toast.error('Please fix the highlighted fields.');
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the automation.';
        setFormError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}
    >
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
          {isEdit ? 'Edit automation' : 'New automation'}
        </h1>
        {!isEdit ? (
          <Link to="/automation-templates" style={{ fontSize: 'var(--font-size-sm)' }}>
            Or start from a ready-made template →
          </Link>
        ) : null}
      </div>

      <AutomationDefinitionEditor
        form={form}
        setForm={setForm}
        errors={errors}
        setErrors={setErrors}
        channels={channels.data ?? []}
        replyOptions={replyOptions}
        lookups={{
          templates: approvedTemplates,
          templatesLoading: templates.isPending,
          flows: flows.data?.items ?? [],
          pipelines: pipelines.data ?? [],
        }}
        newAction={newAction}
        flowPreview={(key) => <TenantFlowPreview flowKey={key} flows={flows.data?.items ?? []} />}
        onJsonValidityChange={setJsonValid}
      />

      {/* Name & status */}
      <Card>
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <TextField
            label="Name"
            placeholder="e.g. Price enquiry reply"
            value={form.name}
            onChange={(e) => {
              set({ name: e.target.value });
              clear('name');
            }}
            error={errors.name}
          />
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={form.status === 'ACTIVE'}
              onChange={(e) =>
                set({
                  status: e.target.checked
                    ? 'ACTIVE'
                    : existing.data?.status === 'ACTIVE'
                      ? 'INACTIVE'
                      : 'DRAFT',
                })
              }
            />
            Switched on (runs for real customers)
          </label>
          {form.status === 'ARCHIVED' ? (
            <span style={muted}>This automation is archived.</span>
          ) : null}
          <LinkButton onClick={() => setShowDetails((s) => !s)} expanded={showDetails}>
            {showDetails ? '▾' : '▸'} Description
          </LinkButton>
          {showDetails ? (
            <TextField
              label="Description (optional)"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              error={errors.description}
            />
          ) : null}
        </div>
      </Card>

      <div
        role="status"
        aria-label="Summary"
        style={{
          ...box,
          background: 'var(--color-surface-raised)',
          borderColor: 'var(--color-primary)',
        }}
      >
        <strong style={muted}>In plain words</strong>
        <span>{summary}</span>
      </div>

      {formError ? (
        <p className="ne-auth__error" role="alert">
          {formError}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create automation'}
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate('/automations')}>
          Cancel
        </Button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automations">← All automations</Link>
      </p>
    </form>
  );
}
